import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { PageHeading } from '~/components/common/PageHeading';
import { Panel } from '~/components/common/Panel';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { OtherWikiFormFields } from '~/components/upload/other-wiki/other-wiki-form-fields';
import { uploadOtherWikiUpdateRoute } from '~/paths';
import { useZodForm } from '~/utils/forms';
import { initialOtherWiki, otherWikiSchema } from '~/utils/modules/other-wiki';

export const CREATE_OTHER_WIKI = graphql(`
  mutation CreateOtherWiki($input: OtherWikiCreateInput!) {
    otherWikiCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

export default function CreateOtherWikiPage() {
  return (
    <ApolloProviderV4>
      <CreateOtherWikiPageInner />
    </ApolloProviderV4>
  );
}

function CreateOtherWikiPageInner() {
  useBreadcrumb('routes/upload/other/create', 'Create Other Wiki');
  const navigate = useNavigate();
  const [createOtherWiki, { loading, error }] = useMutation(CREATE_OTHER_WIKI);

  const form = useZodForm({
    schema: otherWikiSchema,
    values: initialOtherWiki(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      const res = await createOtherWiki({
        variables: {
          input: values,
        },
      });
      const wikiPageId = res.data?.otherWikiCreate?.result?.id;
      invariant(wikiPageId, 'Error parsing create response');
      toast.success('Other Wiki created successfully.');
      navigate(uploadOtherWikiUpdateRoute(parseInt(wikiPageId)));
    } catch (err) {
      console.log('Error creating other wiki', err);
      toast.error('There was a problem creating the other wiki.');
    }
  });

  return (
    <div className="space-y-4">
      <PageHeading>Create Other Wiki</PageHeading>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Panel>
            <Panel.Heading>
              <Panel.Title>Create Other Wiki</Panel.Title>
            </Panel.Heading>

            <Panel.Body className="space-y-4">
              <OtherWikiFormFields />
              <HookFormErrors graphQLError={error} />
            </Panel.Body>

            <Panel.Footer align="right">
              <Button type="submit" color="primary" loading={loading}>
                Save
              </Button>
            </Panel.Footer>
          </Panel>
        </form>
      </FormProvider>
    </div>
  );
}
